import { HttpContextToken } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationModalComponent } from '../components/confirmation-modal/confirmation-modal.component';

// Consultas de apoio (ex.: checar se o Finpac está habilitado para o tenant) não devem abrir
// o modal de acesso negado: com o token, o interceptor devolve o 403 para quem chamou tratar.
export const SKIP_ACCESS_DENIED_MODAL = new HttpContextToken<boolean>(() => false);

export interface AccessDeniedModalData {
  permissionName?: string;
  permissionKey?: string;
  requestUrl?: string;
  method?: string;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class AccessDeniedModalService {
  private dialogRef: DynamicDialogRef | null = null;
  private pendingEntries: AccessDeniedModalData[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly dialogService: DialogService,
    private readonly router: Router,
  ) {}

  open(data?: AccessDeniedModalData): void {
    if (!data) {
      return;
    }

    this.pendingEntries.push(data);

    if (this.dialogRef) {
      return;
    }

    if (this.flushTimer) {
      return;
    }

    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      this.showPendingEntries();
    }, 120);
  }

  private showPendingEntries(): void {
    if (this.dialogRef || this.pendingEntries.length === 0) {
      return;
    }

    const entries = [...this.pendingEntries];
    this.pendingEntries = [];

    const message = this.buildMessage(entries);

    this.dialogRef = this.dialogService.open(ConfirmationModalComponent, {
      showHeader: false,
      width: '34rem',
      closable: false,
      dismissableMask: false,
      closeOnEscape: false,
      modal: true,
      data: {
        title: 'No Permission',
        message,
        confirmLabel: 'Go to home',
        showCancel: true,
        cancelLabel: 'Stay on this page',
        confirmIcon: 'pi pi-check',
        confirmButtonClass:
          'w-full font-bold justify-center !border-none !bg-blue-600 hover:!bg-blue-700 !text-white transition-colors duration-200 py-3 shadow-sm',
        iconContainerClass:
          'w-16 h-16 rounded-full bg-red-50 flex items-center justify-center',
        iconClass: 'pi pi-lock text-3xl text-red-500',
      },
    });

    this.dialogRef.onClose.subscribe((confirmed) => {
      if (confirmed) {
        this.router.navigate(['/home']);
      }
      this.dialogRef = null;

      if (this.pendingEntries.length > 0) {
        this.flushTimer = setTimeout(() => {
          this.flushTimer = null;
          this.showPendingEntries();
        }, 120);
      }
    });
  }

  private buildMessage(entries: AccessDeniedModalData[]): string {
    const intro = entries.length === 1
      ? (entries[0].message || 'You do not have permission to access this resource.')
      : 'You do not have permission to access one or more resources.';

    const requestBlocks = entries
      .map((entry, index) => this.formatEntry(entry, index + 1))
      .join('\n\n');

    return `${intro}${entries.length > 0 ? `\n\n${requestBlocks}` : ''}\n\nYou can stay on this page and use other menus you have access to.`;
  }

  private formatEntry(entry: AccessDeniedModalData, index: number): string {
    const details: string[] = [];
    if (entry.permissionName) details.push(`Name: ${entry.permissionName}`);
    if (entry.permissionKey) details.push(`Key: ${entry.permissionKey}`);
    if (entry.requestUrl) details.push(`URL: ${entry.requestUrl}`);
    if (entry.method) details.push(`Method: ${entry.method}`);

    return `${index}. ${details.join('\n   ')}`;
  }
}
